/**
 * Client-side error reporting: ships an uncaught frontend error to the backend so it lands in the
 * server log (CloudWatch / SST). The browser console only exists on the user's machine — without this,
 * a production crash is invisible. Wired from four places: the router-wide error boundary
 * (`src/route-error.tsx`), the global `error` and `unhandledrejection` handlers (`src/router.tsx`), and
 * React's own `onRecoverableError` / `onUncaughtError` (`src/client.tsx`) — which is the only path that
 * can supply a component stack, and therefore the only one that makes a minified hydration mismatch
 * diagnosable.
 */

import { isChunkLoadError } from '../route-error-classify'
import { isHydrationError } from './hydration-mismatch'
// Statically imported, unlike `@/main` below: neither module touches `window` at module scope, and
// `hydration-mismatch` WANTS to evaluate early — it samples the `<html lang>` the document arrived with,
// and that reading is only trustworthy before a page translator has had a chance to rewrite it.
import { describeHydrationRewrite } from './hydration-rewrite-monitor'

/**
 * Depth of the `cause` chain {@link describeCauseChain} walks. Three is plenty for the wrappers seen in
 * practice, and keeps a cyclic or absurdly deep chain from turning one report into a wall of text.
 */
const MAX_CAUSE_DEPTH = 3

/**
 * The `cause` chain of `error` as its own reportable block, or `undefined` when there is none.
 *
 * A wrapped error keeps the diagnosis in `cause`, and the wrapper's own message is often useless on its
 * own: React recovers from a failed chunk import during concurrent rendering and re-throws a generic
 * `Minified React error #…`, with `Caused by: TypeError: Failed to fetch dynamically imported module: …`
 * underneath. Without this the row says "minified React error" and the line naming the fault is lost.
 *
 * Its OWN field, never folded into `message` or `stack`: both feed the server's error fingerprint (see
 * `computeSignature`), so a per-occurrence cause in either would split one error into a new row — and a
 * new GitHub issue — every time it happened. Fully guarded, because failing to enrich a report must
 * never cost the report itself.
 */
function describeCauseChain(error: unknown): string | undefined {
  const parts: string[] = []
  try {
    let current: unknown = error
    for (let depth = 0; depth < MAX_CAUSE_DEPTH; depth++) {
      if (!(current instanceof Error)) break
      const cause: unknown = current.cause
      if (cause == null) break
      if (cause instanceof Error) {
        parts.push(`Caused by: ${cause.name}: ${cause.message}\n${cause.stack ?? '(no stack)'}`)
      } else {
        parts.push(`Caused by: ${typeof cause === 'string' ? cause : safeStringify(cause)}`)
        break
      }
      current = cause
    }
  } catch {
    // A failure to enrich a report must never lose the report.
  }
  return parts.length === 0 ? undefined : parts.join('\n\n')
}

/**
 * Normalize an arbitrary thrown value into `{ name, message, stack, cause }`. A thrown value can be
 * anything (an Error, a string, null, a tRPC error object, …), so this never assumes `instanceof Error`.
 * Pure and dependency-free so it's unit-testable in isolation.
 */
export function extractErrorInfo(error: unknown): {
  name: string
  message: string
  stack: string | undefined
  cause?: string
} {
  if (error instanceof Error) {
    return {
      name: error.name || 'Error',
      message: error.message || '(empty message)',
      stack: error.stack,
      cause: describeCauseChain(error),
    }
  }
  if (typeof error === 'string') {
    return { name: 'StringError', message: error, stack: undefined }
  }
  if (error == null) {
    return { name: 'NullishError', message: `Thrown value was ${String(error)}`, stack: undefined }
  }
  if (typeof error === 'object') {
    const name = 'name' in error && typeof error.name === 'string' ? error.name : 'ObjectError'
    const message =
      'message' in error && typeof error.message === 'string'
        ? error.message
        : 'error' in error && typeof error.error === 'string'
          ? error.error
          : safeStringify(error)
    const stack = 'stack' in error && typeof error.stack === 'string' ? error.stack : undefined
    return { name, message, stack }
  }
  return { name: 'UnknownError', message: safeStringify(error), stack: undefined }
}

function safeStringify(value: unknown): string {
  try {
    return JSON.stringify(value)
  } catch {
    return String(value)
  }
}

/**
 * Is this a tRPC *client* error (a `TRPCClientError`)? Those are controlled server responses — a known
 * machine `ErrorCode`, a 401, an input-validation failure, even a redacted 500 — that the client already
 * handles in `toastErrorLink` (toast/redirect), and whose server-side throw is already logged where it
 * happened. They are NOT frontend JS exceptions, which is the only thing this reporter exists to capture,
 * so they must never be forwarded to the backend log.
 *
 * Duck-typed rather than `instanceof TRPCClientError`: bundling can give the thrown error a different
 * class identity across module boundaries (same reason `route-error.tsx` duck-types), but
 * `TRPCClientError` always sets `name = 'TRPCClientError'` in its constructor, and carries a `data`
 * payload with the transport `code` — either is a reliable signal.
 */
export function isTrpcClientError(error: unknown): boolean {
  if (typeof error !== 'object' || error == null) return false
  const e = error as { name?: unknown; data?: { code?: unknown } }
  if (e.name === 'TRPCClientError') return true
  return typeof e.data === 'object' && e.data != null && typeof e.data.code === 'string'
}

/** Where an error was caught. `recoverable` is React's own error hooks — see `src/client.tsx`. */
export type FrontendErrorSource = 'boundary' | 'global' | 'unhandledrejection' | 'recoverable'

/**
 * Extra context only some capture sites can supply.
 *
 * `namePrefix` is prepended to the error NAME, which is a fingerprint input server-side (see
 * `computeSignature`), so it deliberately splits one minified React error into separate rows — and
 * separate GitHub issues — by severity class: a mismatch React patched over is not the same problem as
 * one it could not recover from, and merging them hides a broken page behind a cosmetic ticket.
 *
 * `componentStack` rides in `stack`, never in `message`: the message feeds the fingerprint too, and a
 * per-occurrence component path there would mint a fresh issue for every distinct component.
 */
export interface FrontendErrorExtra {
  namePrefix?: string
  componentStack?: string | null | undefined
}

/**
 * Send an error to the backend (persisted + deduped as an `errors` row, and — when enabled — a
 * `client_error` wide event). Fire-and-forget: never throws and never awaits the caller. `source` tags
 * where it was caught. The recent nav/click/console breadcrumb trail is attached so a crash is
 * reproducible; correlation ids (trace/session) ride on the tRPC request headers (see main.tsx).
 * Everything else (identity, browser) is derived server-side.
 *
 * This is also the ONE place a hydration-class error is gated on who rewrote the document. Every
 * capture site funnels through here — the router boundary, the two global handlers and React's own
 * `onRecoverableError` in `src/client.tsx` — precisely so they cannot drift onto different gates, which
 * is how a translator's mismatches kept getting ticketed as ours on the portals codebase this came from.
 */
export function logFrontendError(
  error: unknown,
  source: FrontendErrorSource = 'boundary',
  extra?: FrontendErrorExtra,
): void {
  // Server-side throws are already logged where they happen (trpc.ts / loaders); this path is for the
  // browser only, and `trpcClient` uses window-coupled HTTP links that don't exist on the server.
  if (typeof window === 'undefined') return
  // tRPC client errors are controlled, expected responses (already handled by toastErrorLink and logged
  // server-side), not frontend exceptions — don't ship them to the backend log. See isTrpcClientError.
  if (isTrpcClientError(error)) return
  // A stale chunk after a deploy is version skew, not a defect: the boundary already recovers it by
  // waiting for reachability and reloading (see RecoverableErrorNotice), and `recordError` opens a GitHub
  // issue on an error's first production occurrence — so every deploy that caught a user mid-session
  // filed one. The boundary path never reached here anyway (it checks `recoverable` first); the global
  // `error` and `unhandledrejection` handlers did, with no filter at all. See isChunkLoadError, which
  // reads the cause chain — that is where React leaves the real failure when it recovers on its own.
  if (isChunkLoadError(error)) return
  const info = extractErrorInfo(error)
  // React hands the component stack as `\n    at Component (…)\n    at Parent (…)`. Only the leading
  // blank line is dropped (the label below supplies its own) — a bare `.trim()` would take the first
  // frame's indentation with it and leave the block misaligned against every frame under it.
  const componentStack = extra?.componentStack?.replace(/^\n+/, '').trimEnd()
  // Import lazily to keep the window-coupled `@/main` singletons (and breadcrumbs) out of anything that
  // only wants the pure `extractErrorInfo` (e.g. its unit test). The rewrite gate rides in the same
  // chain: it reads a `window` log the inline recorder left behind and the dehydrated query cache,
  // neither of which decays while a microtask resolves, so deferring it costs nothing and keeps this
  // module's synchronous surface dependency-free.
  Promise.all([import('../main'), import('./breadcrumbs')])
    .then(([{ trpcClient, queryClient }, { getBreadcrumbs }]) => {
      // A hydration mismatch on a document something else already rewrote — a page translator, a
      // content blocker, an in-app-WebView injection — is that rewriter's doing, not ours. Asked only
      // for hydration-class errors, and it fails open, so an unrecognised rewrite still reports.
      const rewrite = isHydrationError(info.message) ? describeHydrationRewrite(queryClient) : undefined
      if (rewrite?.foreign) return
      return trpcClient.common.logFrontendError.mutate({
        route: window.location.pathname,
        name: (extra?.namePrefix ?? '') + info.name,
        message: info.message,
        // Appended, not merged into `message`: see FrontendErrorExtra.
        stack: componentStack ? `${info.stack ?? '(no stack)'}\n\nComponent stack:\n${componentStack}` : info.stack,
        cause: info.cause,
        source,
        breadcrumbs: getBreadcrumbs(),
        // Why a hydration report got PAST the gate. Without it "the page was pristine" and "the check
        // never ran" are the same silence, which is what makes this class of bug undiagnosable.
        counters: rewrite?.attribution,
      })
    })
    .catch(() => {
      // Swallow — reporting the error must never itself surface an error.
    })
}
